<template>
    <div>
        <ModuleTitleNav :next-module="nextModule" :previous-module="previousModule" class="title-one">
            {{ moduleTitle }}
        </ModuleTitleNav>
        <div class="section-line" />
        <fieldset :disabled="formDisabled">
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 135 (d)]
                    </div>
                    <div class="info-text">
                        The college shall not use more than 5 percent of Perkins
                        funds for costs associated with the administration of
                        activities that are necessary for the proper and efficient
                        performance of its duties to fulfill obligations related
                        to the Perkins grant. Use of administrative funds will
                        be described in the Annual Plan. If the college has
                        negotiated an indirect cost rate (ICR), upload it here.
                    </div>
                    <hr>
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col>
                    <b-form-group label="Do you have a negotiated indirect cost rate (ICR)?">
                        <b-form-radio-group v-model="requiresWavierUpload" name="wavierRadio">
                            <b-form-radio :value="1">
                                Yes
                            </b-form-radio>
                            <b-form-radio :value="0">
                                No
                            </b-form-radio>
                        </b-form-radio-group>
                        <b-form-group />
                    </b-form-group>
                </b-col>
            </b-form-row>
        </fieldset>
        <b-form-row class="d-flex flex-column align-items-center">
            <div class="font-weight-bold attached-file-prefix">
                <div v-if="attachedFile.localApplicationUploadId">
                    <p>
                        Currently Attached ICR File:
                        <span class="text-monospace font-weight-normal">
                            {{ attachedFile.originalFileName }}
                        </span>
                    </p>
                    <p class="mt-n3">
                        Uploaded On:
                        <span class="text-monospace font-weight-normal">
                            {{ (new Date(attachedFile.uploadedOn || null)).toLocaleString() }}
                        </span>
                    </p>
                </div>
                <p v-else-if="requiresWavierUpload === 1">
                    No ICR file attached, please upload a file and save
                </p>
            </div>
            <b-button v-if="requiresWavierUpload === 1"
                      class="mb-2"
                      size="sm"
                      variant="outline-primary"
                      :disabled="formDisabled"
                      @click="showUploadModal"
            >
                <fa-icon :icon="['fas', 'upload']" />
                Upload ICR File
            </b-button>
            <b-button v-if="requiresWavierUpload === 1"
                      size="sm"
                      variant="outline-secondary"
                      data-toggle="tooltip"
                      data-placement="right"
                      :disabled="!attachedFile.localApplicationUploadId"
                      :title="attachedFile.originalFileName"
                      @click="downloadFile(attachedFile)"
            >
                <fa-icon :icon="['fas', 'download']" />
                View Attached ICR File
            </b-button>
        </b-form-row>
        <hr>
        <b-row>
            <b-col>
                <LocalApplicationModuleActions
                    :is-loading="isSaving"
                    :is-disabled="formDisabled"
                    background-color="#006438"
                    @saveForm="saveForm"
                />
            </b-col>
        </b-row>

        <!-- file upload modal -->
        <!-- TODO: Extract this to a global component -->
        <b-modal v-if="!formDisabled" ref="fileUploadModalRef" title="Upload File">
            <p clas="my-4">
                Click to select a file, then click the upload button
            </p>
            <div class="d-block text-center">
                <b-form-file id="upload_file"
                             ref="fileUploadRef"
                             v-model="uploadingFile"
                             choose-label="Attachment"
                />
            </div>
            <hr>
            <p><strong>ICR File Selected: </strong>{{ uploadingFile && uploadingFile.name }}</p>
            <div slot="modal-footer" class="w-100">
                <b-btn size="sm" variant="secondary" @click="hideUploadModal">
                    Cancel
                </b-btn>
                <b-btn size="sm" variant="success" @click="uploadFile">
                    Upload
                </b-btn>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';

const storeModuleName = 'administrativeFunds';
// const storeModuleNamespace = 'localApplications/module';

export default {
    name: 'AdministrativeFunds',

    props: {
        // formDisabled: { type: Boolean, default: true },
    },

    data() {
        return {
            isSaving: false,
            uploadingFile: null,

            moduleTitle: 'Administrative Funds',
            // nextModule: { name: 'localApplication.BaselineAndTargets' },
            nextModule: { name: 'localApplication.Section113Instructions' },
            previousModule: { name: 'localApplication.PoolingOfFunds' },
        };
    },

    computed: {
        ...mapGetters({
            isAuthorized: 'auth/isAuthorized',
            hasContentChanges: `localApplication/module/${storeModuleName}/hasContentChanges`,
            localApplicationModuleContent: `localApplication/module/${storeModuleName}/content`,
            parentId: `localApplication/module/${storeModuleName}/parentId`,
        }),

        ...mapState('localApplication', {
            currentState: (state) => state.localApplicationData.state.currentState,
        }),

        ...mapState(`localApplication/module/${storeModuleName}`, {
            uploadedFiles: (state) => state.data.content.uploadedFiles
        }),

        formDisabled() {
            return !this.isAuthorized('update', 'local_application', this.currentState);
        },

        requiresWavierUpload: {
            get() { return this.localApplicationModuleContent.requiresWavierUpload; },
            set(value) { this.updateContentField({ field: 'requiresWavierUpload', value }); }
        },

        attachedFile: {
            get() { return this.localApplicationModuleContent.attachedFile; },
            set(v) { this.setAttachedFile(v); }
        }
    },

    updated() {
        if (this.parentId) {
            this.fetchUploads();
        }
    },

    methods: {
        ...mapActions(`localApplication/module/${storeModuleName}`, {
            saveLocalApplicationModule: 'save',
            setAttachedFile: 'setAttachedFile',
            resetAttachedFile: 'resetAttachedFile',
            uploadLocalApplicationFile: 'uploadFile',
            updateContentField: 'updateContentField',
            downloadFile: 'downloadFile',
            fetchUploads: 'fetchUploads'
        }),

        showUploadModal() {
            this.$refs.fileUploadModalRef.show();
        },

        hideUploadModal() {
            this.$refs.fileUploadRef.reset();
            this.uploadingFile = null;
            this.$refs.fileUploadModalRef.hide();
        },

        async uploadFile() {
            try {
                const data = await this.uploadLocalApplicationFile(this.uploadingFile);
                // NOTE: If this is the first upload, we'll go ahead and save the module
                // if (this.uploadedFiles.length === 0 && this.attachedFile === null) {
                //     this.attachedFile = data;
                //     this.saveForm();
                // }

                // TODO: Temporarily set the attached file, until we implement showing previously uploaded files
                //   and handling upload/selection through a generic component
                this.attachedFile = data;
                this.saveForm();
            } catch (error) {
                console.log(error);
            } finally {
                this.hideUploadModal();
            }
        },

        async saveForm() {
            this.isSaving = true;
            // NOTE: We remove the attachment if they have uploaded something, but choose no later
            if (!this.requiresWavierUpload) {
                await this.resetAttachedFile();
            }

            try {
                const response = await this.saveLocalApplicationModule();
                this.isSaving = false;
                Log.debug(response, `COMPONENT:${this.name} -> Save module response:`);
            } catch (error) {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style scoped>
.attached-file-prefix p {
    font-weight: bold;
    font-size: 0.9rem;
}

</style>
